import { useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import API from '../../config/axiosConfig';
import InputWithIcon from '../../components/InputWithIcon';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import DepartmentSelectionStep from '../../components/DepartmentSelectionStep';
import { hospitalRoles } from '../../config/hospitalRoles';
import { getServerHebMessage } from '../../utils/department-utils';
import roleIcon from '../../assets/users/role.svg';
import userIcon from '../../assets/users/user.svg';
import hospitalIcon from '../../assets/protocols/hospital.svg';
import departmentIcon from '../../assets/protocols/department.svg';

const idOf = (value) => value?._id || value || '';

const TEST_HOSPITAL_KEY = 'mashlom.onboardingTestHospital';

// Same `?test=true` switch as the legacy wizard. It cannot ride through the
// Microsoft redirect, so it is opened on /onboarding/register by hand and then
// remembered for the browser session to survive a refresh or a resume.
const readShowTestHospital = (search) => {
  try {
    if (new URLSearchParams(search).get('test') === 'true') {
      sessionStorage.setItem(TEST_HOSPITAL_KEY, 'true');
      return true;
    }
    return sessionStorage.getItem(TEST_HOSPITAL_KEY) === 'true';
  } catch {
    return false;
  }
};

const EntraOnboardingWizard = () => {
  const { user, lastAuthResult, refreshUserData, refreshAllUserData } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const showTestHospital = readShowTestHospital(location.search);
  const onboarding = user?.entraOnboarding;
  const invitationAssignment = Boolean(user?.roles?.length);
  // Invited users already have a department, and super admins (the Entra
  // SuperAdmin role) need none: both are done after the profile step.
  const finishesAfterProfile = invitationAssignment || Boolean(user?.isSuperAdmin);
  const draft = onboarding?.draft || {};
  const initialStep = onboarding?.status === 'workplace' ? 2 : 1;
  const [step, setStep] = useState(initialStep);
  const [hospitals, setHospitals] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [duplicateErrors, setDuplicateErrors] = useState({});
  const [data, setData] = useState({
    name: user?.name || '',
    hospitalRole: user?.hospitalRole || '',
    termsAccepted: Boolean(user?.termsAcceptance?.acceptedAt),
    hospital: idOf(draft.hospital),
    departmentOption: draft.departmentOption || '',
    selectedDepartment: idOf(draft.selectedDepartment),
    newDepartment: {
      nameHebrew: draft.newDepartment?.nameHebrew || '',
      nameEnglish: draft.newDepartment?.nameEnglish || '',
    },
  });

  const isReturning = lastAuthResult?.isNew === false;
  const greeting = isReturning
    ? `ברוך שובך, ${user?.name}. יש להשלים עוד כמה פרטים כדי לסיים את ההרשמה.`
    : `שלום ${user?.name}, נשארו עוד כמה פרטים כדי להשלים את ההרשמה`;
  const hospitalRoleLabel = hospitalRoles.find(role => role.value === data.hospitalRole)?.label || '';
  const backToProfile = () => {
    setStep(1);
    setError('');
  };
  const selectedHospitalName = useMemo(
    () => hospitals.find(hospital => hospital._id === data.hospital)?.nameHebrew || '',
    [hospitals, data.hospital]
  );

  useEffect(() => {
    if (['completed', 'pending_approval'].includes(onboarding?.status)) {
      navigate('/homepage', { replace: true });
    }
  }, [navigate, onboarding?.status]);

  useEffect(() => {
    API.get('/api/onboarding/hospitals')
      .then(response => setHospitals(showTestHospital
        ? response.data
        : response.data.filter(hospital => hospital.nameEnglish !== 'test hospital')))
      .catch(() => setError('לא הצלחנו לטעון את רשימת בתי החולים. נסו לרענן את העמוד.'));
  }, [showTestHospital]);

  const updateField = (name, value) => {
    setData(previous => ({ ...previous, [name]: value }));
    setFormErrors(previous => ({ ...previous, [name]: '' }));
    setError('');
  };

  // The account is now completed server-side, but the context still holds the
  // in-progress snapshot and none of the department-scoped data. Leave the wizard
  // first (the route guard would otherwise bounce a completed user to /homepage
  // mid-refresh), then pull the whole context forward.
  const finishOnboarding = (outcome) => {
    navigate('/onboarding/thank-you', { replace: true, state: { outcome } });
    refreshAllUserData();
  };

  const submitProfile = async (event) => {
    event.preventDefault();
    const errors = {};
    if (!data.name.trim()) errors.name = 'שדה חובה';
    if (!data.hospitalRole) errors.hospitalRole = 'נא לבחור תפקיד';
    if (!data.termsAccepted) errors.termsAccepted = 'חובה לאשר את תנאי השימוש';
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    setIsSubmitting(true);
    setError('');
    try {
      const response = await API.patch('/api/users/me/entra-onboarding/profile', {
        name: data.name,
        hospitalRole: data.hospitalRole,
        termsAccepted: true,
      }, { handlingErrorLocally: true });
      localStorage.setItem('mashlom.termsSigned', Date.now());
      if (['invitation', 'super_admin'].includes(response.data.outcome)) {
        finishOnboarding(response.data.outcome);
      } else {
        await refreshUserData();
        setStep(2);
      }
    } catch (requestError) {
      setError(getServerHebMessage(requestError) || 'לא הצלחנו לשמור את הפרטים. נסו שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateWorkplace = () => {
    const errors = {};
    if (!data.hospital) errors.hospital = 'נא לבחור בית חולים';
    if (!['join', 'create'].includes(data.departmentOption)) {
      errors.departmentOption = 'יש לבחור אפשרות';
    }
    if (data.departmentOption === 'join' && !data.selectedDepartment) {
      errors.selectedDepartment = 'אנא בחרו מחלקה';
    }
    if (data.departmentOption === 'create') {
      if (!data.newDepartment.nameHebrew.trim()) errors.nameHebrew = 'שדה חובה';
      if (!data.newDepartment.nameEnglish.trim()) errors.nameEnglish = 'שדה חובה';
    }
    setFormErrors(errors);
    return !Object.keys(errors).length && !Object.keys(duplicateErrors).length;
  };

  const workplacePayload = () => ({
    hospital: data.hospital,
    departmentOption: data.departmentOption,
    selectedDepartment: data.selectedDepartment || undefined,
    newDepartment: data.newDepartment,
  });

  const submitWorkplace = async () => {
    if (!validateWorkplace()) return;
    setIsSubmitting(true);
    setError('');
    try {
      await API.patch('/api/users/me/entra-onboarding/workplace', workplacePayload(), {
        handlingErrorLocally: true,
      });
      const response = await API.post('/api/users/me/entra-onboarding/finalize', workplacePayload(), {
        handlingErrorLocally: true,
      });
      finishOnboarding(response.data.outcome);
    } catch (requestError) {
      setError(getServerHebMessage(requestError) || 'לא הצלחנו להשלים את ההרשמה. הפרטים נשמרו ואפשר לנסות שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="entra-onboarding" dir="rtl">
      <h1>{greeting}</h1>

      {!finishesAfterProfile && (
        <div className="wizard-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${step * 50}%` }} />
          </div>
          <div className="step-indicator">שלב {step} מתוך 2</div>
        </div>
      )}

      {step === 1 && (
        <section className="onboarding-step entra-step-card">
          <h2>הפרטים שלך</h2>
          <form onSubmit={submitProfile}>
            <div>
              <InputWithIcon
                icon={userIcon}
                placeholder="שם מלא"
                name="name"
                value={data.name}
                onChange={event => updateField('name', event.target.value)}
                error={formErrors.name}
              />
              {formErrors.name && <div className="error-message">{formErrors.name}</div>}
            </div>
            <div>
              <DropdownWithIcon
                icon={roleIcon}
                placeholder="מה תפקידך?"
                name="hospitalRole"
                value={data.hospitalRole}
                onChange={event => updateField('hospitalRole', event.target.value)}
                error={formErrors.hospitalRole}
              >
                {hospitalRoles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </DropdownWithIcon>
              {formErrors.hospitalRole && <div className="error-message">{formErrors.hospitalRole}</div>}
            </div>

            {invitationAssignment && (
              <div className="entra-assignment">
                <strong>השיוך שלך כבר נקבע בהזמנה</strong>
                <span>{draft.departmentName} · {draft.hospitalName}</span>
                <small>לאחר שמירת הפרטים אין צורך לבחור מחלקה.</small>
              </div>
            )}

            <div className="entra-terms">
              <FormControlLabel
                sx={{ m: 0, gap: 0.5 }}
                control={(
                  <Checkbox
                    checked={data.termsAccepted}
                    onChange={event => updateField('termsAccepted', event.target.checked)}
                  />
                )}
                label={(
                  // A span: the step's `p` rule would blow a paragraph up to 1.5rem.
                  <Typography component="span" sx={{ fontSize: '0.95rem' }}>
                    אני מאשר/ת כי קראתי את{' '}
                    <Link href="https://drive.google.com/file/d/1x8SNHDi9QbS-JYTWLYx7Yz7G68KlhWfM/view" target="_blank" rel="noopener">
                      תנאי השימוש
                    </Link>
                    {' '}ואני מסכים/ה להם
                  </Typography>
                )}
              />
              {formErrors.termsAccepted && <div className="error-message">{formErrors.termsAccepted}</div>}
            </div>
            {error && <div className="error-message" role="alert">{error}</div>}
            <div className="wizard-buttons">
              <LoadingSubmitButton
                className="onboarding-button"
                isLoading={isSubmitting}
                text={finishesAfterProfile ? 'שמירה וסיום' : 'שמירה והמשך'}
                loadingText="שומר..."
              />
            </div>
          </form>
        </section>
      )}

      {step === 2 && (
        <section className="onboarding-step entra-step-card">
          <h2>איפה אתם עובדים?</h2>
          {/* A resumed user lands straight on this step, so show what step 1 saved
              and let them change it. */}
          <div className="entra-profile-summary">
            <div>
              <strong>הפרטים שלך</strong>
              <span>{[data.name, hospitalRoleLabel].filter(Boolean).join(' · ')}</span>
            </div>
            <button type="button" className="entra-summary-edit" onClick={backToProfile} disabled={isSubmitting}>
              עריכה
            </button>
          </div>
          <div className="entra-hospital-field">
            <DropdownWithIcon
              icon={hospitalIcon}
              placeholder="בית חולים"
              name="hospital"
              value={data.hospital}
              onChange={event => {
                updateField('hospital', event.target.value);
                setData(previous => ({
                  ...previous,
                  hospital: event.target.value,
                  departmentOption: '',
                  selectedDepartment: '',
                }));
              }}
              error={formErrors.hospital}
            >
              {hospitals.map(hospital => (
                <option key={hospital._id} value={hospital._id}>{hospital.nameHebrew}</option>
              ))}
            </DropdownWithIcon>
            {formErrors.hospital && <div className="error-message">{formErrors.hospital}</div>}
          </div>
          {data.hospital && (
            <DepartmentSelectionStep
              selectedHospital={data.hospital}
              departmentOption={data.departmentOption}
              onDepartmentOptionChange={value => updateField('departmentOption', value)}
              selectedDepartment={data.selectedDepartment}
              onSelectedDepartmentChange={value => updateField('selectedDepartment', value)}
              newDepartment={data.newDepartment}
              onNewDepartmentChange={(field, value) => {
                setData(previous => ({
                  ...previous,
                  newDepartment: { ...previous.newDepartment, [field]: value },
                }));
                setFormErrors(previous => ({ ...previous, [field]: '' }));
              }}
              onSubmit={submitWorkplace}
              isSubmitting={isSubmitting}
              error={error}
              formErrors={formErrors}
              submitButtonText="סיום הרשמה"
              submitLoadingText="מסיים..."
              showBackButton
              onBack={backToProfile}
              departmentIcon={departmentIcon}
              onDuplicateNameChange={setDuplicateErrors}
              noDepartmentsMessage={`טרם נוצרו מחלקות בבית חולים ${selectedHospitalName}. אפשר ליצור עכשיו את המחלקה הראשונה.`}
            />
          )}
          {!data.hospital && error && <div className="error-message" role="alert">{error}</div>}
          {/* DepartmentSelectionStep carries the back button once a hospital is
              chosen; until then, offer it here. */}
          {!data.hospital && (
            <div className="wizard-buttons">
              <button type="button" className="back-button" onClick={backToProfile}>
                חזור
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default EntraOnboardingWizard;
